import type { TFunction } from 'i18next'
import type { AnimalType, Gender, Temperament } from './constants'

export interface ReportHeadlineInput {
  kind: 'lost' | 'found'
  type: AnimalType
  gender?: Gender | null
  spayed?: 0 | 1 | null
  chip_status?: 'unknown' | 'none' | 'known' | null
  temperament?: Temperament | null
  /** ISO timestamp — used for lost “since …” only. */
  missingSince?: string | null
}

function relativeMissingPhrase(iso: string, locale: string): string | null {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  const diffMs = Date.now() - then
  if (diffMs < 0) return null

  const minutes = Math.round(diffMs / 60_000)
  const hours = Math.round(diffMs / 3_600_000)
  const days = Math.round(diffMs / 86_400_000)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'always' })

  if (minutes < 60) return rtf.format(-Math.max(1, minutes), 'minute')
  if (hours < 48) return rtf.format(-hours, 'hour')
  if (days < 14) return rtf.format(-days, 'day')
  if (days < 60) return rtf.format(-Math.round(days / 7), 'week')
  if (days < 730) return rtf.format(-Math.round(days / 30), 'month')
  return rtf.format(-Math.round(days / 365), 'year')
}

/** Relative “3 hours ago” / “2 days ago” from an ISO timestamp (past only). */
export function formatRelativePast(
  iso: string | null | undefined,
  locale: string,
): string | null {
  if (!iso) return null
  return relativeMissingPhrase(iso, locale)
}

/**
 * Facebook-style headline built only from known facts.
 * Never includes “not sure” / unknown gender / unknown fixed / unknown chip.
 */
export function buildReportHeadline(
  input: ReportHeadlineInput,
  t: TFunction,
  locale: string,
): string {
  const subjectParts: string[] = [t(`headline.${input.kind}`)]
  if (input.gender === 'male' || input.gender === 'female') {
    subjectParts.push(t(`headline.${input.gender}`))
  }
  subjectParts.push(t(`headline.types.${input.type}`))

  const clauses: string[] = [subjectParts.join(' ')]

  const traits: string[] = []
  if (input.spayed === 1) {
    if (input.gender === 'male') traits.push(t('headline.neutered'))
    else if (input.gender === 'female') traits.push(t('headline.spayed'))
    else traits.push(t('headline.fixed'))
  } else if (input.spayed === 0) {
    traits.push(t('headline.notFixed'))
  }
  if (input.chip_status === 'none') traits.push(t('headline.notChipped'))
  else if (input.chip_status === 'known') traits.push(t('headline.chipped'))
  if (traits.length > 0) clauses.push(traits.join(' '))

  if (input.temperament && input.temperament !== 'not_sure') {
    clauses.push(t(`headline.temperament.${input.temperament}`))
  }

  if (input.kind === 'lost' && input.missingSince) {
    const when = relativeMissingPhrase(input.missingSince, locale)
    if (when) clauses.push(t('headline.since', { when }))
  }

  return clauses.join(', ')
}
